import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiCalendar,
  FiCheckCircle,
  FiChevronDown,
  FiEdit2,
  FiRotateCcw,
  FiSend,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import type { ApprovalAction } from "./reviewRules";
import { SectionTitle } from "./ReviewSidePanel";
import { reopenButtonLabel, type DraftComment } from "./useReviewSession";

// Side-panel pieces of the review session: the unsent draft comments and the pinned footer with
// the general comment and the primary actions (Reopen for RN, Submit, Approve / Mark submitted).

/** Which submitted date the reviewer picked from the Submit menu (a custom one is asked next). */
export type SubmitDateChoice = "original" | "custom";

export function DraftCommentList(props: {
  drafts: DraftComment[];
  questionLabels: Map<number, string>;
  onEdit: (questionId: number) => void;
  onRemove: (questionId: number) => void;
}) {
  return (
    <Box>
      <SectionTitle count={props.drafts.length}>Drafts · not sent yet</SectionTitle>
      <Stack spacing={2}>
        {props.drafts.map((draft) => (
          <Box
            key={draft.questionId}
            bg="orange.50"
            borderColor="orange.300"
            borderRadius="md"
            borderStyle="dashed"
            borderWidth={1}
            px={3}
            py={2.5}
          >
            <Text color="gray.500" fontSize="xs" noOfLines={1}>
              {props.questionLabels.get(draft.questionId) ?? `Question ${draft.questionId}`}
            </Text>
            <Text
              color="gray.800"
              fontSize="sm"
              mt={1}
              overflowWrap="anywhere"
              whiteSpace="pre-wrap"
            >
              {draft.text}
            </Text>
            <Flex gap={1} justify="flex-end" mt={1}>
              <Button
                leftIcon={<Icon as={FiEdit2} boxSize={3} />}
                size="xs"
                variant="ghost"
                onClick={() => props.onEdit(draft.questionId)}
              >
                Edit
              </Button>
              <Button
                colorScheme="red"
                leftIcon={<Icon as={FiTrash2} boxSize={3} />}
                size="xs"
                variant="ghost"
                onClick={() => props.onRemove(draft.questionId)}
              >
                Remove
              </Button>
            </Flex>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Submit, with the submitted date to use: a submitted document keeps its original one, a first
 * submission gets today's; either can be replaced by a custom date.
 */
function SubmitMenu(props: {
  blockedReason: string | null;
  isLoading: boolean;
  isSubmitted: boolean;
  onSubmit: (date: SubmitDateChoice) => void;
}) {
  return (
    <Tooltip
      hasArrow
      isDisabled={props.blockedReason === null}
      label={props.blockedReason}
      placement="top"
    >
      <Box>
        <Menu matchWidth placement="top">
          <MenuButton
            as={Button}
            colorScheme="green"
            isDisabled={props.blockedReason !== null}
            isLoading={props.isLoading}
            leftIcon={<Icon as={FiSend} />}
            rightIcon={<Icon as={FiChevronDown} />}
            size="sm"
            w="full"
          >
            Submit
          </MenuButton>
          {/* In a portal: the side panel scrolls and would clip the list */}
          <Portal>
            <MenuList fontSize="sm">
              <MenuItem icon={<Icon as={FiSend} />} onClick={() => props.onSubmit("original")}>
                {props.isSubmitted ? "Submit with original date" : "Submit"}
              </MenuItem>
              <MenuItem icon={<Icon as={FiCalendar} />} onClick={() => props.onSubmit("custom")}>
                Submit with custom date
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </Box>
    </Tooltip>
  );
}

export function ReviewActionsFooter(props: {
  approval: ApprovalAction;
  canReopen: boolean;
  draftCount: number;
  generalComment: string;
  hasUnsentWork: boolean;
  isApproving: boolean;
  isReopening: boolean;
  isSubmitting: boolean;
  /** Why Reopen is disabled even with comments written (e.g. an inline editor is open). */
  reopenBlockedReason: string | null;
  /**
   * The submitted document has changes its PDF does not show: this warning, and Submit to apply
   * them (null = none).
   */
  resubmitNotice: string | null;
  /** Why Submit is disabled (an answer is being edited or saved). */
  submitBlockedReason: string | null;
  onApprove: () => void;
  onGeneralCommentChange: (text: string) => void;
  onMarkSubmitted: () => void;
  onReopen: () => void;
  onSubmit: (date: SubmitDateChoice) => void;
}) {
  const renderApproval = () => {
    switch (props.approval.type) {
      case "approved":
        return (
          <Button
            isDisabled
            colorScheme="blue"
            leftIcon={<Icon as={FiCheckCircle} />}
            size="sm"
            variant="outline"
            w="full"
          >
            Approved
          </Button>
        );
      case "approve":
        return (
          <Button
            colorScheme="blue"
            isDisabled={props.approval.isDisabled}
            isLoading={props.isApproving}
            leftIcon={<Icon as={FiCheckCircle} />}
            size="sm"
            w="full"
            onClick={props.onApprove}
          >
            Approve
          </Button>
        );
      case "markSubmitted":
        return (
          <Button
            colorScheme="green"
            isLoading={props.isApproving}
            leftIcon={<Icon as={FiUpload} />}
            size="sm"
            w="full"
            onClick={props.onMarkSubmitted}
          >
            Mark submitted
          </Button>
        );
      case "submit":
        return (
          <SubmitMenu
            blockedReason={props.submitBlockedReason}
            isLoading={props.isSubmitting}
            isSubmitted={false}
            onSubmit={props.onSubmit}
          />
        );
    }
  };

  const reopenDisabledReason = !props.hasUnsentWork
    ? "Add a comment on a question or a general comment first"
    : props.reopenBlockedReason;

  return (
    <Stack spacing={3}>
      {props.canReopen ? (
        <>
          <FormControl>
            <FormLabel color="gray.600" fontSize="xs" fontWeight="semibold" mb={1}>
              General comment{" "}
              <Text as="span" color="gray.400" fontWeight="normal">
                (optional)
              </Text>
            </FormLabel>
            <Textarea
              bg="white"
              placeholder="A note for the RN about the whole document"
              rows={2}
              size="sm"
              value={props.generalComment}
              onChange={(event) => props.onGeneralCommentChange(event.target.value)}
            />
          </FormControl>
          <Tooltip
            hasArrow
            isDisabled={reopenDisabledReason === null}
            label={reopenDisabledReason}
            placement="top"
          >
            {/* A disabled button fires no pointer events, so the tooltip hangs off this wrapper. */}
            <Box>
              <Button
                colorScheme="orange"
                isDisabled={reopenDisabledReason !== null}
                isLoading={props.isReopening}
                leftIcon={<Icon as={FiRotateCcw} />}
                size="sm"
                variant="outline"
                w="full"
                onClick={props.onReopen}
              >
                {reopenButtonLabel(props.draftCount)}
              </Button>
            </Box>
          </Tooltip>
        </>
      ) : (
        (props.approval.type === "markSubmitted" || props.approval.type === "submit") && (
          <Text color="gray.500" fontSize="xs">
            Comments and reopening for the RN are available once the document is submitted.
          </Text>
        )
      )}
      {props.resubmitNotice !== null && (
        <>
          <Alert
            alignItems="flex-start"
            borderRadius="md"
            fontSize="xs"
            px={3}
            py={2}
            status="warning"
          >
            <AlertIcon boxSize={4} mt={0.5} />
            <AlertDescription>{props.resubmitNotice}</AlertDescription>
          </Alert>
          <SubmitMenu
            blockedReason={props.submitBlockedReason}
            isLoading={props.isSubmitting}
            isSubmitted={true}
            onSubmit={props.onSubmit}
          />
        </>
      )}
      {renderApproval()}
    </Stack>
  );
}
